/**
 * As-built marks on a sheet (spec §15, slice 6): their clickable outline, review status and the review actions a
 * publisher may take. Kept free of Angular so the maths and the rules are unit-testable.
 */
import { DrawingRevision } from '../../../../../../../shared/models/drawing-sheet';
import { AsBuiltReviewStatus, Markup, MarkupReviewEvent } from '../../../../../../../shared/models/markup';

export interface MarkBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface MarkHistoryEntry {
  text: string;
  actor: string;
  at: string | Date | null;
  comment: string | null;
}

export type AsBuiltReviewAction = 'reviewed' | 'rejected' | 'incorporated' | 'open';

export const AS_BUILT_STATUS_COPY: Record<AsBuiltReviewStatus, string> = {
  open: 'Open',
  reviewed: 'Reviewed',
  rejected: 'Rejected',
  incorporated: 'Incorporated',
};

export const AS_BUILT_ACTION_COPY: Record<AsBuiltReviewAction, string> = {
  reviewed: 'Mark reviewed',
  rejected: 'Reject',
  incorporated: 'Mark incorporated',
  open: 'Reopen',
};

/** Where a mark may go from each status (the API's REVIEW_TRANSITIONS), the first being the card's primary action */
const TRANSITIONS: Record<AsBuiltReviewStatus, AsBuiltReviewAction[]> = {
  open: ['reviewed', 'rejected', 'incorporated'],
  reviewed: ['incorporated', 'rejected', 'open'],
  rejected: ['open'],
  incorporated: [],
};

const ORIGIN: Record<string, number> = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };

const finite = (v: unknown, fallback: number | null): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const origin = (v: unknown): number => (typeof v === 'number' ? v : ORIGIN[v as string] ?? 0);
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

export function isAsBuilt(row: Markup | null | undefined): boolean {
  return row?.kind === 'as_built';
}

/**
 * Normalised 0–1 bounding box of a markup's fabric objects against its canvas size; the API's `bboxOf` (as-built
 * listing) mirrored for the per-page rows, which carry no bbox. Each object is width × height scaled by scaleX/scaleY
 * (an ellipse may give rx/ry), rotated by `angle` (degrees, clockwise) about its origin point (originX/originY:
 * keywords or 0–1), then grown by half the stroke width (scaled unless strokeUniform; the larger axis when rotated).
 * Skew and flips are not handled. Null when nothing has a size or the canvas has none.
 */
export function markupBBox(annotationData: { objects?: unknown[] } | null | undefined, canvasWidth: number, canvasHeight: number): MarkBox | null {
  const objects = Array.isArray(annotationData?.objects) ? annotationData.objects : [];
  if (!(canvasWidth > 0) || !(canvasHeight > 0)) {
    return null;
  }
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const item of objects) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const o = item as Record<string, unknown>;
    let width = finite(o.width, null);
    let height = finite(o.height, null);
    if (width === null && height === null && o.type === 'ellipse') {
      width = finite(o.rx, 0) * 2;
      height = finite(o.ry, 0) * 2;
    }
    if (width === null && height === null) {
      continue;
    }
    const sx = Math.abs(finite(o.scaleX, 1));
    const sy = Math.abs(finite(o.scaleY, 1));
    const w = (width ?? 0) * sx;
    const h = (height ?? 0) * sy;
    const stroke = Math.max(0, finite(o.strokeWidth, 0));
    let padX = (o.strokeUniform ? stroke : stroke * sx) / 2;
    let padY = (o.strokeUniform ? stroke : stroke * sy) / 2;
    const rad = (finite(o.angle, 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    if (rad !== 0) {
      padX = Math.max(padX, padY);
      padY = padX;
    }
    const ox = origin(o.originX) * w;
    const oy = origin(o.originY) * h;
    const left = finite(o.left, 0);
    const top = finite(o.top, 0);
    for (const [cx, cy] of [[-ox, -oy], [w - ox, -oy], [w - ox, h - oy], [-ox, h - oy]]) {
      const px = left + cx * cos - cy * sin;
      const py = top + cx * sin + cy * cos;
      x0 = Math.min(x0, px - padX);
      y0 = Math.min(y0, py - padY);
      x1 = Math.max(x1, px + padX);
      y1 = Math.max(y1, py + padY);
    }
  }
  if (x0 === Infinity) {
    return null;
  }
  return { x0: clamp01(x0 / canvasWidth), y0: clamp01(y0 / canvasHeight), x1: clamp01(x1 / canvasWidth), y1: clamp01(y1 / canvasHeight) };
}

/** The status changes a publisher may make from this status, the primary one first */
export function reviewActions(status: AsBuiltReviewStatus | null | undefined): AsBuiltReviewAction[] {
  return status ? TRANSITIONS[status] ?? [] : [];
}

/** The author edits or deletes their own mark only while it is open (spec §15 rule 5) */
export function canAuthorChange(row: Markup | null | undefined, accountUuid: string | null): boolean {
  return isAsBuilt(row) && row.review_status === 'open' && !!accountUuid && row.createdBy?.id === accountUuid;
}

export function authorName(row: Markup | null | undefined): string {
  const by = row?.createdBy;
  return by ? [by.first_name, by.last_name].filter(Boolean).join(' ') : '';
}

/**
 * Revisions of the sheet that came after `fromUuid` (following supersedes_uuid forward) and can incorporate a mark:
 * issued or superseded, nearest first. A withdrawn revision in the chain is passed through but never offered.
 */
export function laterRevisions(revisions: DrawingRevision[] | null | undefined, fromUuid: string | null | undefined): DrawingRevision[] {
  const all = revisions ?? [];
  const found: DrawingRevision[] = [];
  const seen = new Set<string>(fromUuid ? [fromUuid] : []);
  let frontier = fromUuid ? [fromUuid] : [];
  while (frontier.length) {
    const next = all.filter(r => !!r.supersedes_uuid && frontier.includes(r.supersedes_uuid) && !seen.has(r.uuid));
    next.forEach(r => seen.add(r.uuid));
    found.push(...next.filter(r => r.status === 'issued' || r.status === 'superseded'));
    frontier = next.map(r => r.uuid);
  }
  return found;
}

const EVENT_COPY: Record<AsBuiltReviewStatus, string> = {
  open: 'Reopened',
  reviewed: 'Marked reviewed',
  rejected: 'Rejected',
  incorporated: 'Marked incorporated',
};

/**
 * The mark's trail for its card, oldest first: when it was recorded, then every status change. A carried copy's
 * trail starts with its original's events and the carry; it was recorded when the original was.
 */
export function markHistory(row: Markup | null | undefined, events: MarkupReviewEvent[] | null | undefined): MarkHistoryEntry[] {
  if (!isAsBuilt(row)) {
    return [];
  }
  const recorded: MarkHistoryEntry = { text: 'Recorded', actor: authorName(row), at: row.created_at ?? null, comment: null };
  return [recorded, ...(events ?? []).map(e => ({
    text: e.from_status === null ? `Carried forward (${AS_BUILT_STATUS_COPY[e.to_status]?.toLowerCase() ?? e.to_status})`
      : `${EVENT_COPY[e.to_status] ?? e.to_status}${e.carried ? ' (earlier revision)' : ''}`,
    actor: e.actor?.name ?? '',
    at: e.at,
    comment: e.comment,
  }))];
}

export interface MarkCrop {
  /** Image width, as a fraction of the box width */
  width: number;
  /** Image offsets, as fractions of the box width / height */
  left: number;
  top: number;
}

/**
 * A thumbnail of one mark: its strokes image (the page's size, transparent) zoomed so the mark's bbox, padded by
 * `pad` of its size on each side and at least `minSize` of the page, fills the box, centred. Null without a bbox or
 * before the image's aspect is known: show the whole image then.
 * @param imageAspect strokes image width / height (the page's aspect)
 * @param boxAspect thumbnail width / height
 */
export function markCrop(bbox: MarkBox | null | undefined, imageAspect: number, boxAspect: number, pad = 0.2, minSize = 0.08): MarkCrop | null {
  if (!bbox || !(imageAspect > 0) || !(boxAspect > 0)) {
    return null;
  }
  const grow = (lo: number, hi: number) => {
    const size = Math.max(hi - lo, 0);
    const padded = Math.max(size * (1 + 2 * pad), minSize);
    return { centre: (lo + hi) / 2, size: Math.min(padded, 1) };
  };
  const x = grow(bbox.x0, bbox.x1);
  const y = grow(bbox.y0, bbox.y1);
  // Image width in box widths: the region must fit across (x.size * width <= 1) and down
  // (y.size * width / imageAspect <= 1 / boxAspect); never smaller than the box.
  const width = Math.max(1, Math.min(1 / x.size, imageAspect / (y.size * boxAspect)));
  const height = width / imageAspect; // in box widths
  return { width, left: 0.5 - x.centre * width, top: 0.5 - y.centre * height * boxAspect };
}

/** One confirm for deleting a mark, on the sheet card and the As-built tab: a soft delete, so its trail stays */
export function deleteMarkMessage(sheetNumber: string | null | undefined, revisionLabel: string | null | undefined): string {
  const where = sheetNumber ? ` on ${sheetNumber}${revisionLabel ? ` Rev ${revisionLabel}` : ''}` : '';
  return `Delete this as-built mark${where}? It leaves the sheet and the record set; its review history is kept.`;
}

/** 7.2: a record's as-built marks on the page (tagged to its link), newest first */
export function recordAsBuiltRows(rows: Markup[], linkUuid: string): Markup[] {
  return (rows || []).filter(r => r.link_uuid === linkUuid && r.kind === 'as_built')
    .sort((a, b) => time(b.created_at) - time(a.created_at));
}

function time(value: unknown): number {
  return value ? new Date(value as string).getTime() || 0 : 0;
}
